import React, { useMemo, useState } from 'react';
import {
  PlusIcon,
  DropletIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  PencilIcon,
  TrashIcon,
  InfoIcon,
} from 'lucide-react';
import { Toast } from '../Toast';
import { BloodParameterFormModal } from '../blood-parameters/BloodParameterFormModal';
import {
  useClientBloodParameters,
  useCreateBloodParameterReading,
  useUpdateBloodParameterReading,
  useDeleteBloodParameterReading,
  groupReadingsByParameter,
  groupByCategory,
} from '../../hooks/useBloodParameters';
import type {
  BloodParameterReadingWithDefinition,
  BloodParameterFormData,
  GroupedBloodParameter,
} from '../../types/blood-parameters';

interface BloodParametersTabProps {
  clientId: string;
}

function formatDisplayDate(value: string) {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

// Category colors for visual grouping
const CATEGORY_COLORS: Record<string, string> = {
  'Blood Sugar': 'bg-red-100 text-red-700 border-red-200',
  'Lipid Profile': 'bg-orange-100 text-orange-700 border-orange-200',
  'Vitamins': 'bg-yellow-100 text-yellow-700 border-yellow-200',
  'Thyroid Panel': 'bg-purple-100 text-purple-700 border-purple-200',
  'Complete Blood Count': 'bg-pink-100 text-pink-700 border-pink-200',
  'Iron Profile': 'bg-blue-100 text-blue-700 border-blue-200',
  'Kidney Function': 'bg-green-100 text-green-700 border-green-200',
  'Liver Function': 'bg-teal-100 text-teal-700 border-teal-200',
  'Custom': 'bg-gray-100 text-gray-700 border-gray-200',
};

function getCategoryColor(category: string): string {
  return CATEGORY_COLORS[category] || CATEGORY_COLORS['Custom'];
}

// Single parameter card with expandable history
function ParameterCard({
  parameter,
  onEdit,
  onDelete,
}: {
  parameter: GroupedBloodParameter;
  onEdit: (reading: BloodParameterReadingWithDefinition) => void;
  onDelete: (reading: BloodParameterReadingWithDefinition) => void;
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const latest = parameter.latest_reading;
  const hasHistory = parameter.readings.length > 1;

  return (
    <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
      {/* Main content */}
      <div className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <h4 className="font-semibold text-[#212121]">
                {parameter.display_name}
              </h4>
              <span
                className={`text-xs px-2 py-0.5 rounded-full border ${getCategoryColor(
                  parameter.category,
                )}`}
              >
                {parameter.category}
              </span>
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-bold text-[#5B8E87]">
                {latest.value}
              </span>
              <span className="text-sm text-gray-500">{latest.unit}</span>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Tested: {formatDisplayDate(latest.test_date)}
            </p>
            {parameter.reference_range && (
              <div className="flex items-center gap-1 mt-2 text-xs text-blue-600">
                <InfoIcon className="w-3 h-3" />
                <span>Normal: {parameter.reference_range}</span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => onEdit(latest)}
              className="p-2 bg-gray-100 rounded-lg text-gray-600 hover:bg-gray-200"
              aria-label="Edit reading"
            >
              <PencilIcon className="w-4 h-4" />
            </button>
            <button
              onClick={() => onDelete(latest)}
              className="p-2 bg-red-50 rounded-lg text-red-500 hover:bg-red-100"
              aria-label="Delete reading"
            >
              <TrashIcon className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Notes if present */}
        {latest.notes && (
          <p className="text-sm text-gray-600 mt-2 bg-gray-50 p-2 rounded-lg">
            {latest.notes}
          </p>
        )}
      </div>

      {/* Expandable history */}
      {hasHistory && (
        <>
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="w-full px-4 py-2 bg-gray-50 border-t border-gray-100 flex items-center justify-center gap-2 text-sm text-gray-600 hover:bg-gray-100 transition-colors"
          >
            {isExpanded ? (
              <>
                <ChevronUpIcon className="w-4 h-4" />
                Hide History ({parameter.readings.length - 1} previous)
              </>
            ) : (
              <>
                <ChevronDownIcon className="w-4 h-4" />
                View History ({parameter.readings.length - 1} previous)
              </>
            )}
          </button>

          {isExpanded && (
            <div className="border-t border-gray-100">
              {parameter.readings.slice(1).map((reading) => (
                <div
                  key={reading.id}
                  className="px-4 py-3 border-b border-gray-50 last:border-b-0 flex items-center justify-between"
                >
                  <div>
                    <span className="font-medium text-[#212121]">
                      {reading.value} {reading.unit}
                    </span>
                    <span className="text-xs text-gray-500 ml-2">
                      {formatDisplayDate(reading.test_date)}
                    </span>
                    {reading.notes && (
                      <p className="text-xs text-gray-500 mt-0.5">
                        {reading.notes}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => onEdit(reading)}
                      className="p-1.5 text-gray-500 hover:text-gray-700"
                      aria-label="Edit reading"
                    >
                      <PencilIcon className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => onDelete(reading)}
                      className="p-1.5 text-red-400 hover:text-red-600"
                      aria-label="Delete reading"
                    >
                      <TrashIcon className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export function BloodParametersTab({ clientId }: BloodParametersTabProps) {
  const [showForm, setShowForm] = useState(false);
  const [editingReading, setEditingReading] =
    useState<BloodParameterReadingWithDefinition | null>(null);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  // Fetch readings
  const readingsQuery = useClientBloodParameters(clientId);
  const readings = readingsQuery.data ?? [];

  // Group readings by parameter and category
  const groupedParameters = useMemo(
    () => groupReadingsByParameter(readings),
    [readings],
  );
  const byCategory = useMemo(
    () => groupByCategory(groupedParameters),
    [groupedParameters],
  );

  // Filter by selected category
  const filteredByCategory = useMemo(() => {
    if (!selectedCategory) return byCategory;
    return byCategory.filter((c) => c.category === selectedCategory);
  }, [byCategory, selectedCategory]);

  // Get unique categories for filter pills
  const categories = useMemo(
    () => byCategory.map((c) => c.category),
    [byCategory],
  );

  // Mutations
  const createMutation = useCreateBloodParameterReading(clientId);
  const updateMutation = useUpdateBloodParameterReading(
    clientId,
    editingReading?.id ?? '',
  );
  const deleteMutation = useDeleteBloodParameterReading(
    clientId,
    editingReading?.id ?? '',
  );

  const handleOpenCreate = () => {
    setEditingReading(null);
    setShowForm(true);
  };

  const handleOpenEdit = (reading: BloodParameterReadingWithDefinition) => {
    setEditingReading(reading);
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingReading(null);
  };

  const handleSubmit = async (formData: BloodParameterFormData) => {
    const payload = {
      parameter_definition_id: formData.parameter_definition_id || undefined,
      custom_parameter_name: formData.custom_parameter_name || undefined,
      value: parseFloat(formData.value),
      unit: formData.unit,
      test_date: formData.test_date,
      notes: formData.notes || undefined,
    };

    try {
      if (editingReading) {
        await updateMutation.mutateAsync(payload);
        setToastMessage('Blood parameter updated');
      } else {
        await createMutation.mutateAsync(payload);
        setToastMessage('Blood parameter added');
      }
      handleCloseForm();
    } catch {
      // Error handling is done by mutation
    }
  };

  const handleDelete = async (reading: BloodParameterReadingWithDefinition) => {
    if (!window.confirm('Delete this blood parameter reading?')) {
      return;
    }

    try {
      // Create a temporary mutation for the specific reading ID
      const response = await fetch(
        `/api/clients/${clientId}/blood-parameters/${reading.id}`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        },
      );

      if (!response.ok) {
        throw new Error('Failed to delete');
      }

      setToastMessage('Blood parameter deleted');
      readingsQuery.refetch();
    } catch {
      setToastMessage('Failed to delete reading');
    }
  };

  // Empty state
  if (readingsQuery.isLoading) {
    return (
      <div className="p-4">
        <div className="bg-white rounded-2xl shadow-sm p-8 text-center border border-gray-100">
          <p className="text-sm text-gray-500">Loading blood parameters...</p>
        </div>
      </div>
    );
  }

  if (!readings.length) {
    return (
      <div className="p-4">
        <div className="bg-white rounded-2xl shadow-sm p-8 text-center border border-gray-100">
          <div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <DropletIcon className="w-10 h-10 text-[#E57373]" />
          </div>
          <h3 className="text-lg font-semibold text-[#212121] mb-2">
            No blood parameters yet
          </h3>
          <p className="text-gray-600 mb-4">
            Track lab results and monitor health metrics for this client.
          </p>
          <button
            onClick={handleOpenCreate}
            className="bg-[#5B8E87] text-white px-6 py-2 rounded-lg font-medium"
          >
            Add Blood Parameter
          </button>
        </div>

        <button
          onClick={handleOpenCreate}
          className="fixed bottom-24 right-6 bg-[#C9A870] text-white w-14 h-14 rounded-full shadow-lg flex items-center justify-center hover:bg-[#B89660] transition-all active:scale-95 z-20"
        >
          <PlusIcon className="w-6 h-6" />
        </button>

        <BloodParameterFormModal
          isOpen={showForm}
          onClose={handleCloseForm}
          onSubmit={handleSubmit}
          isSubmitting={createMutation.isPending || updateMutation.isPending}
          editingReading={editingReading}
        />

        {toastMessage && (
          <Toast message={toastMessage} onClose={() => setToastMessage(null)} />
        )}
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4">
      {/* Category filter pills */}
      {categories.length > 1 && (
        <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
          <button
            onClick={() => setSelectedCategory(null)}
            className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
              selectedCategory === null
                ? 'bg-[#5B8E87] text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            All
          </button>
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                selectedCategory === category
                  ? 'bg-[#5B8E87] text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {category}
            </button>
          ))}
        </div>
      )}

      {/* Parameters grouped by category */}
      {filteredByCategory.map(({ category, parameters }) => (
        <div key={category}>
          <h3 className="font-semibold text-sm text-gray-500 uppercase tracking-wide mb-3 px-1">
            {category}
          </h3>
          <div className="space-y-3">
            {parameters.map((parameter) => (
              <ParameterCard
                key={
                  parameter.definition?.id ??
                  `custom:${parameter.custom_name}`
                }
                parameter={parameter}
                onEdit={handleOpenEdit}
                onDelete={handleDelete}
              />
            ))}
          </div>
        </div>
      ))}

      {/* FAB */}
      <button
        onClick={handleOpenCreate}
        className="fixed bottom-24 right-6 bg-[#C9A870] text-white w-14 h-14 rounded-full shadow-lg flex items-center justify-center hover:bg-[#B89660] transition-all active:scale-95 z-20"
      >
        <PlusIcon className="w-6 h-6" />
      </button>

      {/* Form Modal */}
      <BloodParameterFormModal
        isOpen={showForm}
        onClose={handleCloseForm}
        onSubmit={handleSubmit}
        isSubmitting={createMutation.isPending || updateMutation.isPending}
        editingReading={editingReading}
      />

      {/* Toast */}
      {toastMessage && (
        <Toast message={toastMessage} onClose={() => setToastMessage(null)} />
      )}
    </div>
  );
}
